<template>
  <div class="novw-create-page">
    <!-- 页面头部 -->
    <div class="novw-page-header">
      <span class="novw-back-btn" @click="handleBack">&lt; 返回</span>
      <span class="novw-page-title">创建待办</span>
      <span class="novw-header-placeholder"></span>
    </div>

    <!-- 表单内容 -->
    <div class="novw-form-container">
      <!-- 基本信息 -->
      <div class="novw-form-section">
        <div class="novw-section-title">基本信息</div>

        <div class="novw-form-item">
          <label class="novw-form-label"><span class="novw-required">*</span>待办类型</label>
          <div class="novw-form-control">
            <select v-model="form.type" class="novw-select">
              <option value="">请选择类型</option>
              <option v-for="item in typeOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
            </select>
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label"><span class="novw-required">*</span>标题</label>
          <div class="novw-form-control">
            <input v-model="form.title" type="text" class="novw-input" placeholder="请输入待办标题" maxlength="100" />
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label"><span class="novw-required">*</span>优先级</label>
          <div class="novw-form-control">
            <div class="novw-radio-group">
              <label
                v-for="item in priorityOptions"
                :key="item.value"
                class="novw-radio-item"
                :class="{ active: form.priority === item.value, [item.class]: true }"
              >
                <input type="radio" v-model="form.priority" :value="item.value" class="novw-radio-input" />
                <span class="novw-radio-label">{{ item.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label"><span class="novw-required">*</span>成本中心</label>
          <div class="novw-form-control">
            <select v-model="form.costCenter" class="novw-select">
              <option value="">请选择成本中心</option>
              <option v-for="item in costCenterOptions" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 审批信息 -->
      <div class="novw-form-section">
        <div class="novw-section-title">审批信息</div>

        <div class="novw-form-item">
          <label class="novw-form-label"><span class="novw-required">*</span>审批人</label>
          <div class="novw-form-control">
            <div class="novw-approver-select" @click="showApproverDialog = true">
              <span v-if="form.approver" class="novw-approver-name">{{ form.approver }}</span>
              <span v-else class="novw-approver-placeholder">请选择审批人</span>
              <span class="novw-approver-arrow">&gt;</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 扩展信息 -->
      <div class="novw-form-section">
        <div class="novw-section-title">扩展信息 <span class="novw-section-tip">（选填）</span></div>

        <div class="novw-form-item">
          <label class="novw-form-label">金额</label>
          <div class="novw-form-control">
            <div class="novw-input-group">
              <input v-model="form.amount" type="number" class="novw-input" placeholder="请输入金额" />
              <span class="novw-input-suffix">元</span>
            </div>
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label">数量</label>
          <div class="novw-form-control novw-form-control-inline">
            <input v-model="form.quantityLabel" type="text" class="novw-input novw-input-small" placeholder="数量标签" />
            <input v-model="form.quantity" type="text" class="novw-input" placeholder="如：5 台" />
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label">时长</label>
          <div class="novw-form-control">
            <input v-model="form.duration" type="text" class="novw-input" placeholder="如：2 小时" />
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label">部门</label>
          <div class="novw-form-control">
            <select v-model="form.department" class="novw-select">
              <option value="">请选择部门</option>
              <option v-for="item in departmentOptions" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label">标签</label>
          <div class="novw-form-control">
            <div class="novw-tags-container">
              <span v-for="(tag, index) in form.tags" :key="index" class="novw-tag-item">
                {{ tag }}
                <span class="novw-tag-remove" @click="removeTag(index)">&times;</span>
              </span>
              <input
                v-model="newTag"
                type="text"
                class="novw-tag-input"
                placeholder="输入标签后按回车添加"
                @keyup.enter="addTag"
              />
            </div>
          </div>
        </div>

        <div class="novw-form-item">
          <label class="novw-form-label">备注说明</label>
          <div class="novw-form-control">
            <textarea v-model="form.remark" class="novw-textarea" placeholder="请输入备注说明" rows="4"></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="novw-form-footer">
      <button class="novw-btn novw-btn-default" @click="handleCancel" :disabled="submitting">取消</button>
      <button class="novw-btn novw-btn-primary" @click="handleSubmit" :disabled="submitting">
        {{ submitting ? '提交中...' : '提交' }}
      </button>
    </div>

    <!-- 审批人选择弹窗 -->
    <div v-if="showApproverDialog" class="novw-dialog-mask" @click.self="showApproverDialog = false">
      <div class="novw-dialog-box novw-approver-dialog">
        <div class="novw-dialog-header">
          <span class="novw-dialog-title">选择审批人</span>
          <span class="novw-dialog-close" @click="showApproverDialog = false">&times;</span>
        </div>
        <div class="novw-dialog-body">
          <div class="novw-approver-search">
            <input v-model="approverKeyword" type="text" class="novw-input" placeholder="搜索姓名" />
          </div>
          <div class="novw-approver-list">
            <div
              v-for="item in filteredApprovers"
              :key="item.id"
              class="novw-approver-item"
              :class="{ active: form.approver === item.name }"
              @click="selectApprover(item)"
            >
              <span class="novw-approver-avatar">{{ item.name.substring(0, 1) }}</span>
              <div class="novw-approver-info">
                <span class="novw-approver-item-name">{{ item.name }}</span>
                <span class="novw-approver-dept">{{ item.department }}</span>
              </div>
              <span v-if="form.approver === item.name" class="novw-approver-check">✓</span>
            </div>
          </div>
        </div>
        <div class="novw-dialog-footer">
          <button class="novw-btn novw-btn-default" @click="showApproverDialog = false">取消</button>
          <button class="novw-btn novw-btn-primary" @click="confirmApprover">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createTodo } from '@/api/nb'

export default {
  name: 'TodoCreatePage',
  data() {
    return {
      submitting: false,
      form: {
        type: '',
        title: '',
        priority: '',
        costCenter: '',
        approver: '',
        amount: '',
        quantityLabel: '数量',
        quantity: '',
        duration: '',
        department: '',
        tags: [],
        remark: ''
      },
      newTag: '',
      showApproverDialog: false,
      approverKeyword: '',
      selectedApprover: null,
      // 类型选项
      typeOptions: [
        { value: '预警', label: '预警' },
        { value: '审批', label: '审批' },
        { value: '任务', label: '任务' },
        { value: '通知', label: '通知' },
        { value: '项目', label: '项目' }
      ],
      // 优先级选项
      priorityOptions: [
        { value: '紧急', label: '紧急', class: 'priority-urgent' },
        { value: '严重', label: '严重', class: 'priority-serious' },
        { value: '普通', label: '普通', class: 'priority-normal' },
        { value: '较低', label: '较低', class: 'priority-low' }
      ],
      // 成本中心选项
      costCenterOptions: [
        'IT 运维部',
        '行政管理部',
        '综合行政部',
        '人力资源部',
        '工程项目部',
        '财务部',
        '市场部'
      ],
      // 部门选项
      departmentOptions: [
        '人事行政部',
        'IT 运维部',
        '行政管理部',
        '综合行政部',
        '人力资源部',
        '工程项目部',
        '财务部',
        '市场部'
      ],
      // 审批人列表
      approverList: [
        { id: 1, name: '王磊', department: 'IT 运维部' },
        { id: 2, name: '刘芳', department: '行政管理部' },
        { id: 3, name: '陈伟', department: '综合行政部' },
        { id: 4, name: '李娜', department: '人力资源部' },
        { id: 5, name: '赵强', department: '工程项目部' },
        { id: 6, name: '张明', department: '财务部' },
        { id: 7, name: '周杰', department: '市场部' }
      ]
    }
  },
  computed: {
    filteredApprovers() {
      if (!this.approverKeyword) {
        return this.approverList
      }
      return this.approverList.filter(item => item.name.includes(this.approverKeyword) || item.department.includes(this.approverKeyword))
    }
  },
  methods: {
    handleBack() {
      this.$router.back()
    },
    handleCancel() {
      this.$router.back()
    },
    addTag() {
      const tag = this.newTag.trim()
      if (tag && !this.form.tags.includes(tag)) {
        this.form.tags.push(tag)
      }
      this.newTag = ''
    },
    removeTag(index) {
      this.form.tags.splice(index, 1)
    },
    selectApprover(item) {
      this.selectedApprover = item
      this.form.approver = item.name
    },
    confirmApprover() {
      this.showApproverDialog = false
    },
    validateForm() {
      if (!this.form.type) {
        alert('请选择待办类型')
        return false
      }
      if (!this.form.title.trim()) {
        alert('请输入待办标题')
        return false
      }
      if (!this.form.priority) {
        alert('请选择优先级')
        return false
      }
      if (!this.form.costCenter) {
        alert('请选择成本中心')
        return false
      }
      if (!this.form.approver) {
        alert('请选择审批人')
        return false
      }
      return true
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      if (this.submitting) {
        return
      }
      // 构造提交数据
      const submitData = {
        type: this.form.type,
        title: this.form.title,
        priority: this.form.priority,
        costCenter: this.form.costCenter,
        approver: this.form.approver,
        tags: this.form.tags
      }
      // 添加可选字段
      if (this.form.amount) {
        submitData.amount = this.formatAmount(this.form.amount) + ' 元'
      }
      if (this.form.quantity) {
        submitData.quantityLabel = this.form.quantityLabel || '数量'
        submitData.quantity = this.form.quantity
      }
      if (this.form.duration) {
        submitData.duration = this.form.duration
      }
      if (this.form.department) {
        submitData.department = this.form.department
      }
      if (this.form.remark) {
        submitData.remark = this.form.remark
      }

      console.log('提交数据:', submitData)
      this.submitting = true

      try {
        const res = await createTodo(submitData)
        console.log('创建待办结果:', res)
        if (res.success) {
          // 跳转到「待我处理」tab（index=0）
          this.$router.push('/todo')
        } else {
          alert('创建失败: ' + (res.errorMsg || '未知错误'))
        }
      } catch (err) {
        console.error('创建待办失败:', err)
        alert('创建失败: ' + (err.message || '网络错误'))
      } finally {
        this.submitting = false
      }
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    formatAmount(amount) {
      return Number(amount).toLocaleString('zh-CN')
    }
  }
}
</script>

<style lang="less" scoped>
.novw-create-page {
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
  box-sizing: border-box;
}

/* 页面头部 */
.novw-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.novw-back-btn {
  font-size: 14px;
  color: #2E5BFF;
  cursor: pointer;
  min-width: 60px;

  &:hover {
    color: #5A7FFF;
  }
}

.novw-page-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.novw-header-placeholder {
  min-width: 60px;
}

/* 表单容器 */
.novw-form-container {
  padding: 16px;
}

/* 表单分组 */
.novw-form-section {
  background: #fff;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 16px;
}

.novw-section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.novw-section-tip {
  font-size: 12px;
  color: #999;
  font-weight: normal;
}

/* 表单项 */
.novw-form-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.novw-form-label {
  width: 100px;
  flex-shrink: 0;
  font-size: 14px;
  color: #333;
  line-height: 36px;
}

.novw-required {
  color: #ff4d4f;
  margin-right: 4px;
}

.novw-form-control {
  flex: 1;
}

.novw-form-control-inline {
  display: flex;
  gap: 12px;
}

/* 输入框 */
.novw-input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #bfbfbf;
  }

  &:focus {
    border-color: #2E5BFF;
  }
}

.novw-input-small {
  width: 120px;
  flex-shrink: 0;
}

.novw-input-group {
  display: flex;
  align-items: center;
}

.novw-input-suffix {
  margin-left: 8px;
  font-size: 14px;
  color: #666;
}

/* 下拉框 */
.novw-select {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  background: #fff;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    border-color: #2E5BFF;
  }
}

/* 文本域 */
.novw-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: #bfbfbf;
  }

  &:focus {
    border-color: #2E5BFF;
  }
}

/* 单选按钮组 */
.novw-radio-group {
  display: flex;
  gap: 12px;
}

.novw-radio-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &.active {
    border-color: #2E5BFF;
    background: #EEF2FF;
  }

  &.priority-urgent.active {
    border-color: #ff4d4f;
    background: #fff1f0;
    .novw-radio-label { color: #ff4d4f; }
  }

  &.priority-serious.active {
    border-color: #fa8c16;
    background: #fff7e6;
    .novw-radio-label { color: #fa8c16; }
  }

  &.priority-normal.active {
    border-color: #2E5BFF;
    background: #EEF2FF;
    .novw-radio-label { color: #2E5BFF; }
  }

  &.priority-low.active {
    border-color: #52c41a;
    background: #f6ffed;
    .novw-radio-label { color: #52c41a; }
  }
}

.novw-radio-input {
  display: none;
}

.novw-radio-label {
  font-size: 14px;
  color: #666;
}

/* 审批人选择 */
.novw-approver-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    border-color: #2E5BFF;
  }
}

.novw-approver-name {
  font-size: 14px;
  color: #333;
}

.novw-approver-placeholder {
  font-size: 14px;
  color: #bfbfbf;
}

.novw-approver-arrow {
  font-size: 12px;
  color: #999;
}

/* 标签输入 */
.novw-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-height: 36px;
  box-sizing: border-box;

  &:focus-within {
    border-color: #2E5BFF;
  }
}

.novw-tag-item {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: #f0f0f0;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

.novw-tag-remove {
  margin-left: 4px;
  cursor: pointer;
  color: #999;

  &:hover {
    color: #ff4d4f;
  }
}

.novw-tag-input {
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  font-size: 14px;

  &::placeholder {
    color: #bfbfbf;
  }
}

/* 底部按钮 */
.novw-form-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #e8e8e8;
}

.novw-btn {
  padding: 10px 32px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.novw-btn-default {
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;

  &:hover {
    border-color: #2E5BFF;
    color: #2E5BFF;
  }
}

.novw-btn-primary {
  background: #2E5BFF;
  color: #fff;
  border: none;

  &:hover {
    background: #5A7FFF;
  }
}

/* 弹窗 */
.novw-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.novw-dialog-box {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.novw-approver-dialog {
  width: 480px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.novw-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.novw-dialog-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.novw-dialog-close {
  font-size: 20px;
  color: #999;
  cursor: pointer;

  &:hover {
    color: #666;
  }
}

.novw-dialog-body {
  padding: 16px 24px;
  flex: 1;
  overflow-y: auto;
}

.novw-approver-search {
  margin-bottom: 16px;
}

.novw-approver-list {
  max-height: 300px;
  overflow-y: auto;
}

.novw-approver-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  &.active {
    background: #EEF2FF;
  }
}

.novw-approver-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #2E5BFF;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: 12px;
}

.novw-approver-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.novw-approver-item-name {
  font-size: 14px;
  color: #333;
}

.novw-approver-dept {
  font-size: 12px;
  color: #999;
  margin-top: 2px;
}

.novw-approver-check {
  color: #2E5BFF;
  font-size: 16px;
}

.novw-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}
</style>
